import { createContext, ReactElement, useContext, useMemo, useRef } from 'react';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { visitTranslationKeys } from '../../translations';

import {
   ExternalId,
   ForGuardian,
   GenericVisitDocumentCreateDraftCommand,
   VisitDocumentCommandExecutor,
   VisitId,
} from './VisitDocumentContext.types';

type VisitDocumentContextType = {
   executeVisitDocumentCommand: VisitDocumentCommandExecutor;
};

const VisitDocumentContext = createContext<VisitDocumentContextType | null>(null);

export const useVisitDocumentContext = (): VisitDocumentContextType => {
   const context = useContext(VisitDocumentContext);

   if (context === null) {
      throw new Error('useVisitDocumentContext must be used within VisitDocumentContextProvider!');
   }

   return context;
};

type VisitDocumentContextProviderProps = {
   readonly children: ReactElement;
   readonly externalId: ExternalId | undefined;
   readonly forGuardian?: ForGuardian;
   readonly visitId: VisitId;
};

export const VisitDocumentContextProvider = (props: VisitDocumentContextProviderProps): ReactElement => {
   const externalIdRef = useRef(props.externalId);

   const { executeAsync: createVisitDocumentExternalId } = useCqrsCommand<
      ExternalId,
      new () => GenericVisitDocumentCreateDraftCommand
   >(
      (VisitDocumentCreateDraftCommand) => {
         const command = new VisitDocumentCreateDraftCommand();
         command.visitId = props.visitId;

         if (props.forGuardian) {
            command.forGuardian = props.forGuardian;
         }

         return command;
      },
      {
         onSuccess: (newExternalId) => {
            externalIdRef.current = newExternalId;
         },
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(visitTranslationKeys.draftSaveFail);
         },
      }
   );

   const contextValue = useMemo<VisitDocumentContextType>(
      () => ({
         executeVisitDocumentCommand: async (VisitDocumentCreateDraftCommand, callback) => {
            const externalId =
               externalIdRef.current ?? (await createVisitDocumentExternalId(VisitDocumentCreateDraftCommand));
            return callback(externalId);
         },
      }),
      [createVisitDocumentExternalId]
   );

   return <VisitDocumentContext.Provider value={contextValue}>{props.children}</VisitDocumentContext.Provider>;
};
